import React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CLINIC_TIME_ZONE } from "../landingscreen/cards/visitflow";

dayjs.extend(utc);
dayjs.extend(timezone);

/* Shared bits of the user module's look (user.css). Presentation only - no
   call changes anything the page did before. */

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.9,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

export const SearchGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <circle cx="11" cy="11" r="7" />
    <line x1="16.5" y1="16.5" x2="21" y2="21" />
  </svg>
);
export const PlusGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke} strokeWidth={2.2}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
export const MailGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <polyline points="3.5 6.5 12 13 20.5 6.5" />
  </svg>
);
export const PencilGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17z" />
    <line x1="14.5" y1="6.5" x2="17.5" y2="9.5" />
  </svg>
);
export const TrashGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <polyline points="4 6.5 20 6.5" />
    <path d="M9 6.5V4.5h6v2" />
    <path d="M6.5 6.5 7.5 20h9l1-13.5" />
    <line x1="10.5" y1="10" x2="10.5" y2="16.5" />
    <line x1="13.5" y1="10" x2="13.5" y2="16.5" />
  </svg>
);
export const CameraGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <path d="M3 8.5h3.5L8 6h8l1.5 2.5H21v10H3z" />
    <circle cx="12" cy="13" r="3.2" />
  </svg>
);
export const CheckGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke} strokeWidth={2.4}>
    <polyline points="4 12.5 9.5 18 20 6" />
  </svg>
);
export const EyeGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
export const EyeOffGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <path d="M10.6 5.6A9.7 9.7 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.7 3.4M6.6 6.8C3.9 8.5 2.5 12 2.5 12S6 18.5 12 18.5c1.8 0 3.3-.5 4.6-1.3" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" />
  </svg>
);
export const KeyGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <circle cx="7.5" cy="16.5" r="3.6" />
    <line x1="10.1" y1="13.9" x2="19.5" y2="4.5" />
    <line x1="15.5" y1="8.5" x2="18" y2="11" />
    <line x1="18" y1="6" x2="20.5" y2="8.5" />
  </svg>
);
export const UnlockGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.2" />
    <path d="M8 10.5V7.5a4 4 0 0 1 7.6-1.7" />
    <line x1="12" y1="14.5" x2="12" y2="16.5" />
  </svg>
);
// The sign-in sheet's sort arrow: drawn pointing down, flipped for ascending.
export const SortArrowGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M12 5v14M6 13l6 6 6-6" />
  </svg>
);

// The three states the page has always drawn, from the same two columns.
export const statusOf = (u) => {
  const status = String(u?.status || "").trim().toUpperCase();
  const locked = String(u?.locked_status || "").trim().toUpperCase();
  if (status === "I") return "Inactive";
  if (locked === "I") return "Locked";
  return "Active";
};

export const initialsOf = (u) =>
  `${String(u?.firstname || "").charAt(0)}${String(u?.lastname || "").charAt(0)}` || "?";

// A colour family per role, falling back to neutral for a practice's own roles.
export const roleClass = (role) => {
  const r = String(role || "").toLowerCase();
  if (r.includes("admin")) return "r-admin";
  if (r.includes("provider") || r.includes("doctor") || r.includes("physician")) return "r-provider";
  if (r.includes("nurse") || r.includes("medical assistant") || r.includes("ma")) return "r-nurse";
  if (r.includes("front") || r.includes("desk") || r.includes("reception")) return "r-frontdesk";
  if (r.includes("service") || r.includes("system")) return "r-service";
  return "";
};

/* "5 min ago" for the recent past, the date beyond a week. The raw stamp stays
   on the cell's title, so nothing the column used to print is lost. */
export const lastActive = (value) => {
  if (!value) return { text: "Never", title: "", none: true };
  // last_login is a naive server-local stamp, so it is read in the
  // clinic's zone rather than the viewer's - otherwise every row is off
  // by the offset between them, and west of the server it reads future.
  const d = dayjs.tz(String(value).replace("T", " "), CLINIC_TIME_ZONE);
  if (!d.isValid()) return { text: String(value).replace("T", " "), title: String(value), none: false };
  const mins = Math.floor((Date.now() - d.valueOf()) / 60000);
  const title = d.format("MMM D, YYYY h:mm A");
  if (mins < 0) return { text: d.format("MMM D, YYYY"), title, none: false };
  if (mins < 1) return { text: "Just now", title, none: false };
  if (mins < 60) return { text: `${mins} min ago`, title, none: false };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return { text: `${hrs} hr${hrs === 1 ? "" : "s"} ago`, title, none: false };
  const days = Math.floor(hrs / 24);
  if (days === 1) return { text: "Yesterday", title, none: false };
  if (days < 7) return { text: `${days} days ago`, title, none: false };
  return { text: d.format("MMM D, YYYY"), title, none: false };
};
